import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  CpuChipIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  SpeakerWaveIcon,
  UserCircleIcon,
  MusicalNoteIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { ThemeToggle } from './ThemeToggle';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { collapsed, toggleCollapsed } = useSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Different navigation based on user role
  const getNavigation = () => {
    const baseNav = [
      { name: 'Dashboard', href: '/', icon: HomeIcon },
    ];

    if (user?.role === 'engineer' || user?.role === 'admin') {
      return [
        ...baseNav,
        { name: 'Monitoring', href: '/monitoring', icon: SpeakerWaveIcon },
        { name: 'Venues', href: '/venues', icon: BuildingOfficeIcon },
        { name: 'Devices', href: '/devices', icon: CpuChipIcon },
        { name: 'Recordings', href: '/recordings', icon: MusicalNoteIcon },
        { name: 'Users', href: '/users', icon: UserGroupIcon },
        { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon },
        { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
      ];
    } else if (user?.role === 'venue_owner') {
      return [
        ...baseNav,
        { name: 'Monitoring', href: '/monitoring', icon: SpeakerWaveIcon },
        { name: 'Devices', href: '/devices', icon: CpuChipIcon },
        { name: 'Recordings', href: '/recordings', icon: MusicalNoteIcon },
        { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon },
        { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
      ];
    } else {
      return [
        ...baseNav,
        { name: 'Monitoring', href: '/monitoring', icon: SpeakerWaveIcon },
        { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon },
        { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
      ];
    }
  };

  const navigation = getNavigation();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      {/* Top bar - mobile only */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-gray-800 dark:bg-gray-950 border-b border-gray-700 dark:border-gray-800 flex items-center justify-between px-4 z-30">
        <span className="text-white text-xl font-bold">AudioMap Cloud</span>
        <div className="flex items-center space-x-2">
          <ThemeToggle />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 rounded-md text-gray-400 dark:text-gray-500 hover:text-white hover:bg-gray-700 dark:hover:bg-gray-800 transition-colors"
          >
            {mobileOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 mt-16"
          onClick={closeMobile}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 lg:top-0 left-0 h-full bg-gray-800 dark:bg-gray-950 border-r border-gray-700 dark:border-gray-800
          transition-all duration-300 ease-in-out z-50
          ${collapsed ? 'w-16' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
      >
        {/* Logo and collapse button - desktop only */}
        <div className="hidden lg:flex items-center justify-between h-16 px-4 border-b border-gray-700 dark:border-gray-800">
          {!collapsed && (
            <span className="text-white text-xl font-bold">AudioMap Cloud</span>
          )}
          <button
            onClick={toggleCollapsed}
            className="p-2 rounded-md text-gray-400 dark:text-gray-500 hover:text-white hover:bg-gray-700 dark:hover:bg-gray-800 transition-colors ml-auto"
          >
            {collapsed ? (
              <ChevronRightIcon className="h-5 w-5" />
            ) : (
              <ChevronLeftIcon className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Mobile header */}
        <div className="lg:hidden flex items-center h-16 px-4 border-b border-gray-700 dark:border-gray-800">
          <span className="text-white text-xl font-bold">Menu</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1 px-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={closeMobile}
                  className={`
                    flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                    ${active
                      ? 'bg-gray-900 dark:bg-gray-800 text-white'
                      : 'text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white'
                    }
                    ${collapsed ? 'justify-center' : ''}
                  `}
                  title={collapsed ? item.name : undefined}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${!collapsed && 'mr-3'}`} />
                  {!collapsed && <span>{item.name}</span>}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Bottom section - Profile and logout */}
        <div className="border-t border-gray-700 dark:border-gray-800 p-2">
          {/* Theme toggle - desktop only */}
          <div className="hidden lg:flex mb-2 px-3 py-2">
            <ThemeToggle />
            {!collapsed && <span className="ml-3 text-sm text-gray-300">Theme</span>}
          </div>

          {/* Profile */}
          <Link
            to="/profile"
            onClick={closeMobile}
            className={`
              flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
              text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white
              ${collapsed ? 'justify-center' : ''}
            `}
            title={collapsed ? 'Profile' : undefined}
          >
            <UserCircleIcon className={`h-5 w-5 flex-shrink-0 ${!collapsed && 'mr-3'}`} />
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">
                  {user?.full_name || user?.email}
                </div>
                <div className="text-xs text-gray-400 capitalize">
                  {user?.role === 'venue_owner' ? 'Owner' : user?.role}
                </div>
              </div>
            )}
          </Link>

          {/* Logout */}
          <button
            onClick={() => {
              closeMobile();
              logout();
            }}
            className={`
              w-full flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
              text-gray-300 dark:text-gray-400 hover:bg-gray-700 dark:hover:bg-gray-800 hover:text-white
              ${collapsed ? 'justify-center' : ''}
            `}
            title={collapsed ? 'Logout' : undefined}
          >
            <ArrowRightOnRectangleIcon className={`h-5 w-5 flex-shrink-0 ${!collapsed && 'mr-3'}`} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
